import React, { useEffect, useRef, useState } from "react";
import { FaCheck, FaFileExcel, FaTimes, FaCaretDown } from "react-icons/fa";
import axios from "axios";
import * as XLSX from "xlsx";
import { API_BASE_URL } from "../../utils/CONSTANTS";
import { toast } from "react-toastify";
import Header from "../../components/header";

const Register = () => {
  const [filters, setFilters] = useState({
    startDate: new Date().toISOString().split("T")[0],
    endDate: new Date().toISOString().split("T")[0],
  });

  const [data, setData] = useState({
    orders: [],
    packing: [],
    dispatch: [],
  });
  const ordersFetchId = useRef(0);

  // --- Sorting state & logic for three tables ---
  const [ordersSort, setOrdersSort] = useState({ key: null, direction: "asc" });
  const [packingSort, setPackingSort] = useState({ key: null, direction: "asc" });
  const [dispatchSort, setDispatchSort] = useState({ key: null, direction: "asc" });

  const handleSort = (which, key) => {
    // which: "orders" | "packing" | "dispatch"
    const setter = which === "orders" ? setOrdersSort : which === "packing" ? setPackingSort : setDispatchSort;
    const current = which === "orders" ? ordersSort : which === "packing" ? packingSort : dispatchSort;

    let direction = "asc";
    if (current.key === key && current.direction === "asc") direction = "desc";
    setter({ key, direction });
  };

  const sortedOrders = React.useMemo(() => {
    const list = [...(data.orders || [])];
    const cfg = ordersSort;
    if (!cfg.key) return list;
    const k = cfg.key, dir = cfg.direction;
    list.sort((a, b) => {
      const aVal = a?.[k] ?? "";
      const bVal = b?.[k] ?? "";
      if (!isNaN(Number(aVal)) && !isNaN(Number(bVal))) return dir === "asc" ? Number(aVal) - Number(bVal) : Number(bVal) - Number(aVal);
      if (k.toLowerCase().includes("date")) {
        const ta = aVal ? new Date(aVal).getTime() : 0;
        const tb = bVal ? new Date(bVal).getTime() : 0;
        return dir === "asc" ? ta - tb : tb - ta;
      }
      return dir === "asc" ? String(aVal).localeCompare(String(bVal)) : String(bVal).localeCompare(String(aVal));
    });
    return list;
  }, [data.orders, ordersSort]);

  const sortedPacking = React.useMemo(() => {
    const list = [...(data.packing || [])];
    const cfg = packingSort;
    if (!cfg.key) return list;
    const k = cfg.key, dir = cfg.direction;
    list.sort((a, b) => {
      const aVal = a?.[k] ?? "";
      const bVal = b?.[k] ?? "";
      if (!isNaN(Number(aVal)) && !isNaN(Number(bVal))) return dir === "asc" ? Number(aVal) - Number(bVal) : Number(bVal) - Number(aVal);
      if (k.toLowerCase().includes("date")) {
        const ta = aVal ? new Date(aVal).getTime() : 0;
        const tb = bVal ? new Date(bVal).getTime() : 0;
        return dir === "asc" ? ta - tb : tb - ta;
      }
      return dir === "asc" ? String(aVal).localeCompare(String(bVal)) : String(bVal).localeCompare(String(aVal));
    });
    return list;
  }, [data.packing, packingSort]);

  const sortedDispatch = React.useMemo(() => {
    const list = [...(data.dispatch || [])];
    const cfg = dispatchSort;
    if (!cfg.key) return list;
    const k = cfg.key, dir = cfg.direction;
    list.sort((a, b) => {
      const aVal = a?.[k] ?? "";
      const bVal = b?.[k] ?? "";
      if (!isNaN(Number(aVal)) && !isNaN(Number(bVal))) return dir === "asc" ? Number(aVal) - Number(bVal) : Number(bVal) - Number(aVal);
      if (k.toLowerCase().includes("date")) {
        const ta = aVal ? new Date(aVal).getTime() : 0;
        const tb = bVal ? new Date(bVal).getTime() : 0;
        return dir === "asc" ? ta - tb : tb - ta;
      }
      return dir === "asc" ? String(aVal).localeCompare(String(bVal)) : String(bVal).localeCompare(String(aVal));
    });
    return list;
  }, [data.dispatch, dispatchSort]);
  // --- end sorting logic ---

  useEffect(() => {
    getOrders();
    getDispatch();
    getPackage();
  }, [filters]);

  const handleFilterChange = (name, value) => {
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const getPackage = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/report/packingall`, {
        params: filters,
        withCredentials: true,
      });
      setData((prev) => ({ ...prev, packing: response.data.packages }));
    } catch (error) {
      if (axios.isAxiosError(error)) {
        toast.error(error?.response?.data?.message);
      }
    }
  };

  const getOrders = async () => {
    const fetchId = ++ordersFetchId.current;

    try {
      const response = await axios.get(`${API_BASE_URL}/api/report/orderall`, {
        params: {
          startDate: filters.startDate,
          endDate: filters.endDate,
          withPackages: "true",
        },
      });

      if (fetchId !== ordersFetchId.current) {
        return;
      }

      setData((prev) => ({
        ...prev,
        orders: response.data.orders || [],
      }));
    } catch (error) {
      if (fetchId !== ordersFetchId.current) {
        return;
      }

      console.error("Error fetching orders:", error);
      if (axios.isAxiosError(error)) {
        toast.error(error.response?.data?.message || "Failed to load order data");
      } else {
        toast.error("Failed to load order data");
      }
    }
  };

  const getDispatch = async () => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/report/dispatchall?startDate=${filters.startDate}&endDate=${filters.endDate}`,
        {
          withCredentials: true,
        }
      );
      setData((prev) => ({ ...prev, dispatch: response.data.dispatchRecords }));
    } catch (error) {
      if (axios.isAxiosError(error)) {
        toast.error(error?.response?.data?.message);
      }
    }
  };

  const handleShow = async () => {
    try {
      getOrders();
      getDispatch();
      getPackage();
    } catch (err) {
      console.error("Error fetching data:", err);
    }
  };

  const handleExport = () => {
    const orderdata = data.orders.map((row) => ({
      "Card.No.": row.cardNo,
      Date: new Date(row.date).toLocaleDateString().split("T")[0],
      Party: row.partySpec,
      "Drg No.": row.drgNo,
      "Order Qty": row.quantity,
      "Pack V.No.": row.pkgvno,
      "Pack Qty": row.pkgqty,
    }));

    const packingData = data.packing.map((row) => ({
      Party: row.partyName,
      "Card No.": row.cardNo,
      "Drg. No.": row.drgNo,
      "Pack Qty": row.totalquantity,
      Date: row.date?.split("T")[0],
      Weight: row.wt,
      Packer: row.packerName,
    }));

    const dispatchData = data.dispatch.map((row) => ({
      "Dispatch V.No.": row.vno,
      Party: row.partyName,
      "Card No": row.cardNo,
      "Description": row.description,
      "Pack Qty": row.qty,
      Date: row.Date?.split("T")[0],
      Weight: row.weight,
    }));

    const workbook = XLSX.utils.book_new();

    const ordersheet = XLSX.utils.json_to_sheet(orderdata);
    const packagesheet = XLSX.utils.json_to_sheet(packingData);
    const dispatchsheet = XLSX.utils.json_to_sheet(dispatchData);

    XLSX.utils.book_append_sheet(workbook, ordersheet, "Order Report");
    XLSX.utils.book_append_sheet(workbook, packagesheet, "Package Report");
    XLSX.utils.book_append_sheet(workbook, dispatchsheet, "Dispatch Report");

    XLSX.writeFile(workbook, "Report.xlsx");
  };

  return (
    <>
      <Header />

      <div className="p-2 max-w-[1200px] mx-auto">
        <div className="bg-[#e6ffff] border border-gray-300 rounded shadow">
          {/* Header */}
          <div className="flex justify-between items-center px-2 py-1 border-b border-gray-300">
            <h2 className="text-sm font-semibold">Register</h2>
            <div className="flex gap-1">
              <button className="p-0.5 hover:bg-gray-200 rounded">
                <FaTimes className="text-gray-600 w-3 h-3" />
              </button>
            </div>
          </div>

          {/* Order Section */}
          <div>
            <div className="bg-teal-600 text-white px-2 py-1 text-xs font-medium flex items-center">
              Order :-
              <div className="flex items-center gap-2 ml-4">
                <div className="flex items-center gap-1">
                  <span>Start</span>
                  <div className="relative">
                    <input
                      type="date"
                      value={filters.startDate}
                      onChange={(e) =>
                        handleFilterChange("startDate", e.target.value)
                      }
                      className="border rounded p-1 pr-6 text-xs w-24 text-black"
                    />
                    <FaCaretDown className="absolute right-1.5 top-2 text-gray-400 w-2 h-2" />
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <span>End</span>
                  <div className="relative">
                    <input
                      type="date"
                      value={filters.endDate}
                      onChange={(e) =>
                        handleFilterChange("endDate", e.target.value)
                      }
                      className="border rounded p-1 pr-6 text-xs w-24 text-black"
                    />
                    <FaCaretDown className="absolute right-1.5 top-2 text-gray-400 w-2 h-2" />
                  </div>
                </div>
              </div>
            </div>
            <div className="p-2">
              <div className="border rounded bg-white">
                <table className="w-full text-xs overflow-x-auto">
                  <thead>
                    <tr className="border-b">
                      <th onClick={() => handleSort("orders", "cardNo")} className="border-r p-1 text-left font-medium cursor-pointer">
                        Card No. {ordersSort.key === "cardNo" ? (ordersSort.direction === "asc" ? "▲" : "▼") : ""}
                      </th>
                      <th onClick={() => handleSort("orders", "date")} className="border-r p-1 text-left font-medium cursor-pointer">
                        Date {ordersSort.key === "date" ? (ordersSort.direction === "asc" ? "▲" : "▼") : ""}
                      </th>
                      <th onClick={() => handleSort("orders", "partySpec")} className="border-r p-1 text-left font-medium cursor-pointer">
                        Party {ordersSort.key === "partySpec" ? (ordersSort.direction === "asc" ? "▲" : "▼") : ""}
                      </th>
                      <th onClick={() => handleSort("orders", "drgNo")} className="border-r p-1 text-left font-medium cursor-pointer">
                        Drg No. {ordersSort.key === "drgNo" ? (ordersSort.direction === "asc" ? "▲" : "▼") : ""}
                      </th>
                      <th onClick={() => handleSort("orders", "quantity")} className="border-r p-1 text-left font-medium cursor-pointer">
                        Order Qty {ordersSort.key === "quantity" ? (ordersSort.direction === "asc" ? "▲" : "▼") : ""}
                      </th>
                      <th onClick={() => handleSort("orders", "pkgvno")} className="border-r p-1 text-left font-medium cursor-pointer">
                        Pack V.No. {ordersSort.key === "pkgvno" ? (ordersSort.direction === "asc" ? "▲" : "▼") : ""}
                      </th>
                      <th onClick={() => handleSort("orders", "pkgdate")} className="border-r p-1 text-left font-medium cursor-pointer">
                        Date {ordersSort.key === "pkgdate" ? (ordersSort.direction === "asc" ? "▲" : "▼") : ""}
                      </th>
                      <th onClick={() => handleSort("orders", "pkgqty")} className="p-1 text-left font-medium cursor-pointer">
                        Pack Qty {ordersSort.key === "pkgqty" ? (ordersSort.direction === "asc" ? "▲" : "▼") : ""}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedOrders.map((row, index) => (
                      <tr key={index} className="border-b">
                        <td className="border-r p-1">{row.cardNo}</td>
                        <td className="border-r p-1">{row.date?.split("T")[0]}</td>
                        <td className="border-r p-1">{row.partySpec}</td>
                        <td className="border-r p-1">{row.drgNo}</td>
                        <td className="border-r p-1">{row.quantity}</td>
                        <td className="border-r p-1">{row.pkgvno}</td>
                        <td className="border-r p-1">{row.pkgdate?.split("T")[0]}</td>
                        <td className="p-1">{row.pkgqty}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Packing Section */}
          <div>
            <div className="bg-teal-600 text-white px-2 py-1 text-xs font-medium">
              Packing :-
            </div>
            <div className="p-2">
              <div className="border rounded bg-white">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b">
                      <th onClick={() => handleSort("packing", "partyName")} className="border-r p-1 text-left font-medium cursor-pointer">
                        Party {packingSort.key === "partyName" ? (packingSort.direction === "asc" ? "▲" : "▼") : ""}
                      </th>
                      <th onClick={() => handleSort("packing", "cardNo")} className="border-r p-1 text-left font-medium cursor-pointer">
                        Card No. {packingSort.key === "cardNo" ? (packingSort.direction === "asc" ? "▲" : "▼") : ""}
                      </th>
                      <th onClick={() => handleSort("packing", "drgNo")} className="border-r p-1 text-left font-medium cursor-pointer">
                        Drg. No. {packingSort.key === "drgNo" ? (packingSort.direction === "asc" ? "▲" : "▼") : ""}
                      </th>
                      <th onClick={() => handleSort("packing", "totalquantity")} className="border-r p-1 text-left font-medium cursor-pointer">
                        Pack Qty {packingSort.key === "totalquantity" ? (packingSort.direction === "asc" ? "▲" : "▼") : ""}
                      </th>
                      <th onClick={() => handleSort("packing", "date")} className="border-r p-1 text-left font-medium cursor-pointer">
                        Date {packingSort.key === "date" ? (packingSort.direction === "asc" ? "▲" : "▼") : ""}
                      </th>
                      <th onClick={() => handleSort("packing", "wt")} className="border-r p-1 text-left font-medium cursor-pointer">
                        Wt. {packingSort.key === "wt" ? (packingSort.direction === "asc" ? "▲" : "▼") : ""}
                      </th>
                      <th onClick={() => handleSort("packing", "packerName")} className="p-1 text-left font-medium cursor-pointer">
                        Packer {packingSort.key === "packerName" ? (packingSort.direction === "asc" ? "▲" : "▼") : ""}
                      </th>
                    </tr>
                  </thead>

                  <tbody>
                    {sortedPacking.map((row, index) => (
                      <tr key={index} className="border-b">
                        <td className="border-r p-1">{row.partyName}</td>
                        <td className="border-r p-1">{row.cardNo}</td>
                        <td className="border-r p-1">{row.drgNo}</td>
                        <td className="border-r p-1">{row.totalquantity}</td>
                        <td className="border-r p-1">{row.date?.split("T")[0]}</td>
                        <td className="border-r p-1">{row.wt}</td>
                        <td className="p-1">{row.packerName}</td>
                      </tr>
                    ))}
                  </tbody>

                </table>
              </div>
            </div>
          </div>

          {/* Dispatch Section */}
          <div>
            <div className="bg-teal-600 text-white px-2 py-1 text-xs font-medium">
              Dispatch :-
            </div>
            <div className="p-2">
              <div className="border rounded bg-white">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b">
                      <th onClick={() => handleSort("dispatch", "vno")} className="border-r p-1 text-left font-medium cursor-pointer">
                        Dis V.No. {dispatchSort.key === "vno" ? (dispatchSort.direction === "asc" ? "▲" : "▼") : ""}
                      </th>
                      <th onClick={() => handleSort("dispatch", "partyName")} className="border-r p-1 text-left font-medium cursor-pointer">
                        Party {dispatchSort.key === "partyName" ? (dispatchSort.direction === "asc" ? "▲" : "▼") : ""}
                      </th>
                      <th onClick={() => handleSort("dispatch", "cardNo")} className="border-r p-1 text-left font-medium cursor-pointer">
                        Card No. {dispatchSort.key === "cardNo" ? (dispatchSort.direction === "asc" ? "▲" : "▼") : ""}
                      </th>
                      <th onClick={() => handleSort("dispatch", "description")} className="border-r p-1 text-left font-medium cursor-pointer">
                        Description {dispatchSort.key === "description" ? (dispatchSort.direction === "asc" ? "▲" : "▼") : ""}
                      </th>
                      <th onClick={() => handleSort("dispatch", "qty")} className="border-r p-1 text-left font-medium cursor-pointer">
                        Pack Qty {dispatchSort.key === "qty" ? (dispatchSort.direction === "asc" ? "▲" : "▼") : ""}
                      </th>
                      <th onClick={() => handleSort("dispatch", "Date")} className="border-r p-1 text-left font-medium cursor-pointer">
                        Date {dispatchSort.key === "Date" ? (dispatchSort.direction === "asc" ? "▲" : "▼") : ""}
                      </th>
                      <th onClick={() => handleSort("dispatch", "weight")} className="p-1 text-left font-medium cursor-pointer">
                        Wt. {dispatchSort.key === "weight" ? (dispatchSort.direction === "asc" ? "▲" : "▼") : ""}
                      </th>
                    </tr>
                  </thead>

                  <tbody>
                    {sortedDispatch.map((row, index) => (
                      <tr key={index} className="border-b">
                        <td className="border-r p-1">{row.vno}</td>
                        <td className="border-r p-1">{row.partyName}</td>
                        <td className="border-r p-1">{row.cardNo}</td>
                        <td className="border-r p-1">{row.description}</td>
                        <td className="border-r p-1">{row.qty}</td>
                        <td className="border-r p-1">{row.Date?.split("T")[0]}</td>
                        <td className="p-1">{row.weight}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex justify-between p-2 bg-teal-600">
            <button
              onClick={handleShow}
              className="flex items-center gap-1 bg-white px-3 py-1 rounded hover:bg-gray-50 text-xs">
              <FaCheck className="text-green-600 w-3 h-3" />
              <span>Show</span>
            </button>

            <button
              onClick={handleExport}
              className="flex items-center gap-1 bg-white px-3 py-1 rounded hover:bg-gray-50 text-xs">
              <FaFileExcel className="text-green-600 w-3 h-3" />
              <span>Export to Excel</span>
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default Register;
